import { useEffect, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronLeft, ChevronRight, Plus, Square } from "lucide-react";
import { api } from "@/api/client";
import { useEntries, useTimer, invalidateWorkspace } from "@/features/data";
import { queryClient } from "@/app/query";
import { useSession, useWorkspaceId } from "@/app/session";
import type { EntryPage, TimeEntry } from "@/api/types";
import { Page } from "@/components/Page";
import { Spinner, Button, ColorDot, ErrorState, IconButton } from "@/components/ui";
import { SegmentedControl } from "@/components/SegmentedControl";
import { EntryDialog, editEntryState, type EntryDialogState } from "@/components/EntryDialog";
import { useToast, errorMessage } from "@/components/Toast";
import { secondsToHMM, elapsedSeconds } from "@/lib/format";
import { isoDate, startOfDay, addDays, startOfWeek, isoWeek } from "@/lib/dates";
import {
  HOUR_PX,
  DAY_MIN,
  SNAP_MIN,
  minToHHMM,
  assignLanes,
  useCalendarDrag,
  type CalendarGhost,
} from "./useCalendarDrag";
import { cn } from "@/lib/cn";

const VIEW_KEY = "timer.calview";
type View = "day" | "work" | "week";

function periodDays(view: View, anchor: Date, weekStart: number): Date[] {
  if (view === "day") return [startOfDay(anchor)];
  const start = startOfWeek(anchor, weekStart);
  const n = view === "work" ? 5 : 7;
  return Array.from({ length: n }, (_, i) => addDays(start, i));
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function entryStartMin(e: TimeEntry): number {
  const s = new Date(e.start_at);
  return s.getHours() * 60 + s.getMinutes();
}

/** minutesToISO converts a visible day + minute-of-day to a UTC instant.
 * min=1440 rolls cleanly to next-day midnight via Date normalization. */
function minutesToISO(day: Date, min: number): string {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, min, 0, 0).toISOString();
}

export function CalendarPage() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const me = useSession().me;
  const weekStart = me?.user.week_start ?? 1;
  const [view, setView] = useState<View>(() => (localStorage.getItem(VIEW_KEY) as View) || (window.innerWidth < 768 ? "day" : "work"));
  const [anchor, setAnchor] = useState(() => new Date());
  const [dialog, setDialog] = useState<EntryDialogState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    // Open scrolled to the working morning.
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_PX;
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const days = useMemo(() => periodDays(view, anchor, weekStart), [view, anchor, weekStart]);
  const from = isoDate(days[0]);
  const to = isoDate(days[days.length - 1]);
  const { data, isLoading, isError, refetch } = useEntries(from, to);
  const { data: running } = useTimer();

  const byDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const e of data?.items ?? []) {
      if (e.running) continue;
      const key = isoDate(new Date(e.start_at));
      const list = map.get(key);
      if (list) list.push(e);
      else map.set(key, [e]);
    }
    return map;
  }, [data]);

  /** patchEntry PUTs new times with an optimistic cache update, reverting on error. */
  async function patchEntry(entry: TimeEntry, startISO: string, endISO: string) {
    if (Date.parse(startISO) === Date.parse(entry.start_at) && entry.end_at && Date.parse(endISO) === Date.parse(entry.end_at)) return;
    const entriesKey = ["entries", wid, from, to];
    await queryClient.cancelQueries({ queryKey: entriesKey });
    const prev = queryClient.getQueryData<EntryPage>(entriesKey);
    const durationSec = Math.round((Date.parse(endISO) - Date.parse(startISO)) / 1000);
    queryClient.setQueryData<EntryPage>(entriesKey, (p) =>
      p ? { ...p, items: p.items.map((i) => (i.id === entry.id ? { ...i, start_at: startISO, end_at: endISO, duration_sec: durationSec } : i)) } : p,
    );
    try {
      await api.put(`/workspaces/${wid}/time-entries/${entry.id}`, {
        description: entry.description,
        project_id: entry.project_id,
        task_id: entry.task_id,
        rate_class_id: entry.rate_class_id,
        billable: entry.billable,
        tags: entry.tags.map((t) => t.id),
        start_at: startISO,
        end_at: endISO,
      });
      invalidateWorkspace(wid);
    } catch (err) {
      queryClient.setQueryData(entriesKey, prev);
      toast(errorMessage(err), "error");
    }
  }

  async function stopTimer() {
    try {
      await api.post(`/workspaces/${wid}/timer/stop`);
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  const { ghost, hiddenEntryId, dragActive, columnPointerDown, blockPointerDown } = useCalendarDrag({
    days,
    gridRef,
    canDrag: (e) => !e.locked && e.user_id === me?.user.id,
    onClickCreate: (dayIdx, slotMin) =>
      setDialog({ date: isoDate(days[dayIdx]), start: minToHHMM(slotMin), end: minToHHMM(Math.min(slotMin + 60, DAY_MIN - 1)) }),
    onDragCreate: (dayIdx, startMin, endMin) =>
      setDialog({ date: isoDate(days[dayIdx]), start: minToHHMM(startMin), end: endMin >= DAY_MIN ? "23:59" : minToHHMM(endMin) }),
    onClickEntry: (e) => setDialog(editEntryState(e)),
    onMove: (e, dayIdx, startMin) => {
      const startISO = minutesToISO(days[dayIdx], startMin);
      const endISO = new Date(Date.parse(startISO) + e.duration_sec * 1000).toISOString();
      void patchEntry(e, startISO, endISO);
    },
    onResize: (e, endMin) => {
      void patchEntry(e, e.start_at, minutesToISO(startOfDay(new Date(e.start_at)), endMin));
    },
  });

  const span = view === "day" ? 1 : 7;
  const week = isoWeek(days[0]).week;
  const todayIso = isoDate(new Date());
  const dayTotal = view === "day" ? (byDay.get(isoDate(days[0])) ?? []).reduce((s, e) => s + e.duration_sec, 0) : 0;
  const label =
    view === "day"
      ? `${days[0].toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}${dayTotal > 0 ? ` · ${secondsToHMM(dayTotal)}` : ""}`
      : `${days[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${days[days.length - 1].toLocaleDateString(undefined, { day: "numeric", month: "short" })} · W${week}`;

  function newEntryNow() {
    const n = new Date();
    const nowMin = n.getHours() * 60 + n.getMinutes();
    const start = Math.min(Math.ceil(nowMin / SNAP_MIN) * SNAP_MIN, DAY_MIN - SNAP_MIN);
    setDialog({ date: isoDate(n), start: minToHHMM(start), end: minToHHMM(Math.min(start + 60, DAY_MIN - 1)) });
  }

  return (
    <Page
      title="Calendar"
      wide
      actions={
        <div className="flex items-center gap-2">
          <SegmentedControl
            options={[
              { value: "day", label: "Day" },
              { value: "work", label: "Work week" },
              { value: "week", label: "Week" },
            ] as const}
            value={view}
            onChange={setView}
          />
          <button onClick={() => setAnchor(new Date())} className="rounded-md border border-line px-3 py-1 text-[13px] text-muted hover:text-ink">Today</button>
          <IconButton label={view === "day" ? "Previous day" : "Previous week"} onClick={() => setAnchor((a) => addDays(a, -span))}><ChevronLeft className="size-4" /></IconButton>
          <span className="min-w-44 text-center text-sm font-medium">{label}</span>
          <IconButton label={view === "day" ? "Next day" : "Next week"} onClick={() => setAnchor((a) => addDays(a, span))}><ChevronRight className="size-4" /></IconButton>
          <Button size="sm" onClick={newEntryNow}><Plus className="size-4" /> Entry</Button>
        </div>
      }
    >
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <div className="rounded-lg border border-line bg-surface">
          {/* day headers */}
          <div className="flex border-b border-line">
            <div className="w-14 shrink-0" />
            {days.map((d) => {
              const isToday = isoDate(d) === todayIso;
              return (
                <div key={isoDate(d)} className={cn("flex-1 border-l border-line py-2 text-center", isToday && "bg-elevated/40")}>
                  <div className="text-[11px] uppercase tracking-wide text-faint">{WD[d.getDay()]}</div>
                  <div className={cn("mono text-sm tabular-nums", isToday ? "font-semibold text-accent" : "text-ink")}>{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* scrollable time grid */}
          <div ref={scrollRef} className="max-h-[68vh] overflow-y-auto">
            <div ref={gridRef} className={cn("flex", dragActive && "cursor-ns-resize select-none")} style={{ height: 24 * HOUR_PX }}>
              {/* hour axis */}
              <div className="w-14 shrink-0">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="mono relative text-[10px] tabular-nums text-faint" style={{ height: HOUR_PX }}>
                    <span className="absolute right-2 -top-1.5">{h > 0 ? minToHHMM(h * 60) : ""}</span>
                  </div>
                ))}
              </div>
              {/* day columns */}
              {days.map((d, di) => (
                <DayColumn
                  key={isoDate(d)}
                  dayIdx={di}
                  entries={byDay.get(isoDate(d)) ?? []}
                  isToday={isoDate(d) === todayIso}
                  nowTick={nowTick}
                  running={running && isoDate(new Date(running.start_at)) === isoDate(d) ? running : null}
                  ghost={ghost && ghost.dayIdx === di ? ghost : null}
                  hiddenEntryId={hiddenEntryId}
                  canDrag={(e) => !e.locked && e.user_id === me?.user.id}
                  onColumnPointerDown={columnPointerDown}
                  onBlockPointerDown={blockPointerDown}
                  onEditEntry={(e) => setDialog(editEntryState(e))}
                  onStopTimer={stopTimer}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {dialog && <EntryDialog state={dialog} onClose={() => setDialog(null)} />}
    </Page>
  );
}

function DayColumn({
  dayIdx,
  entries,
  isToday,
  nowTick,
  running,
  ghost,
  hiddenEntryId,
  canDrag,
  onColumnPointerDown,
  onBlockPointerDown,
  onEditEntry,
  onStopTimer,
}: {
  dayIdx: number;
  entries: TimeEntry[];
  isToday: boolean;
  nowTick: number;
  running: TimeEntry | null;
  ghost: CalendarGhost | null;
  hiddenEntryId: string | null;
  canDrag: (e: TimeEntry) => boolean;
  onColumnPointerDown: (dayIdx: number, ev: React.PointerEvent) => void;
  onBlockPointerDown: (entry: TimeEntry, dayIdx: number, mode: "move" | "resize", ev: React.PointerEvent) => void;
  onEditEntry: (e: TimeEntry) => void;
  onStopTimer: () => void;
}) {
  const now = new Date(nowTick);
  const nowTop = isToday ? ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_PX : -1;

  const laned = useMemo(
    () =>
      assignLanes(
        entries.filter((e) => e.id !== hiddenEntryId),
        (e) => {
          const s = entryStartMin(e);
          return [s, s + Math.max(SNAP_MIN, e.duration_sec / 60)];
        },
      ),
    [entries, hiddenEntryId],
  );

  return (
    <div className={cn("relative flex-1 border-l border-line", isToday && "bg-elevated/20")} onPointerDown={(ev) => onColumnPointerDown(dayIdx, ev)}>
      {/* hour gridlines */}
      {Array.from({ length: 24 }, (_, h) => (
        <div key={h} className="border-b border-line/50 hover:bg-elevated/30" style={{ height: HOUR_PX }} />
      ))}
      {/* current-time marker */}
      {nowTop >= 0 && (
        <div className="pointer-events-none absolute left-0 right-0 z-10 border-t border-accent" style={{ top: nowTop }}>
          <span className="absolute -left-1 -top-1 size-2 rounded-full bg-accent" />
        </div>
      )}
      {/* entry blocks, overlaps laid out side by side */}
      {laned.map(({ item: e, lane, lanes }) => {
        const startMin = entryStartMin(e);
        const durMin = Math.max(SNAP_MIN, e.duration_sec / 60);
        const color = e.project_color ?? "var(--c-accent)";
        const height = (durMin / 60) * HOUR_PX - 2;
        const draggable = canDrag(e);
        return (
          <div
            key={e.id}
            role="button"
            tabIndex={0}
            onPointerDown={(ev) => onBlockPointerDown(e, dayIdx, "move", ev)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                onEditEntry(e);
              }
            }}
            className={cn("absolute overflow-hidden rounded-md border-l-2 px-1.5 py-0.5 text-left text-[11px] leading-tight", draggable ? "cursor-grab" : "cursor-pointer")}
            style={{
              top: (startMin / 60) * HOUR_PX + 1,
              height,
              left: `calc(${(lane / lanes) * 100}% + 3px)`,
              width: `calc(${100 / lanes}% - 6px)`,
              borderLeftColor: color,
              backgroundColor: `color-mix(in oklab, ${color} 20%, var(--c-surface))`,
            }}
          >
            <span className="block truncate font-medium text-ink">{e.details_hidden ? "Hidden" : e.description || "No description"}</span>
            <span className="mono text-faint">{secondsToHMM(e.duration_sec)}</span>
            {draggable && height >= 18 && (
              <div onPointerDown={(ev) => onBlockPointerDown(e, dayIdx, "resize", ev)} className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize" />
            )}
          </div>
        );
      })}
      {/* the running timer as a live, growing block */}
      {running && <RunningBlock entry={running} nowTick={nowTick} onStop={onStopTimer} />}
      {/* drag ghost */}
      {ghost && <GhostBlock ghost={ghost} />}
    </div>
  );
}

function RunningBlock({ entry, nowTick, onStop }: { entry: TimeEntry; nowTick: number; onStop: () => void }) {
  const startMin = entryStartMin(entry);
  const now = new Date(nowTick);
  const startedToday = isoDate(new Date(entry.start_at)) === isoDate(now);
  const endMin = startedToday ? Math.max(startMin + 6, now.getHours() * 60 + now.getMinutes()) : DAY_MIN;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          onPointerDown={(ev) => ev.stopPropagation()}
          className="absolute inset-x-1 z-10 overflow-hidden rounded-md border-l-2 border-running px-1.5 py-0.5 text-left text-[11px] leading-tight"
          style={{
            top: (startMin / 60) * HOUR_PX + 1,
            height: ((endMin - startMin) / 60) * HOUR_PX - 2,
            backgroundColor: "color-mix(in oklab, var(--c-running) 22%, var(--c-surface))",
          }}
        >
          <span className="flex items-center gap-1.5 font-medium text-ink">
            <span className="size-1.5 shrink-0 rounded-full bg-running record-live" aria-hidden />
            <span className="truncate">{entry.description || "No description"}</span>
          </span>
          <span className="mono text-running">{secondsToHMM(elapsedSeconds(entry.start_at, nowTick))}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="right" sideOffset={8} className="z-50 w-64 rounded-lg border border-line bg-elevated p-3 shadow-xl">
          <p className="truncate text-sm font-medium text-ink">{entry.description || "No description"}</p>
          {entry.project_name && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted">
              <ColorDot color={entry.project_color ?? "#888"} /> {entry.project_name}
            </p>
          )}
          <div className="mt-2 flex items-center justify-between">
            <span role="timer" className="mono text-lg font-medium tabular-nums text-running">{secondsToHMM(elapsedSeconds(entry.start_at, nowTick))}</span>
            <Button size="sm" variant="secondary" onClick={onStop} className="gap-1.5 border-running/40 text-running hover:bg-running/10">
              <Square className="size-3.5 fill-current" /> Stop
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function GhostBlock({ ghost }: { ghost: CalendarGhost }) {
  const color = ghost.entry?.project_color ?? "var(--c-accent)";
  return (
    <div
      className="pointer-events-none absolute inset-x-1 z-20 rounded-md border border-dashed px-1.5 py-0.5 text-[11px] leading-tight"
      style={{
        top: (ghost.startMin / 60) * HOUR_PX + 1,
        height: Math.max(10, ((ghost.endMin - ghost.startMin) / 60) * HOUR_PX - 2),
        borderColor: color,
        backgroundColor: `color-mix(in oklab, ${color} 25%, var(--c-surface))`,
      }}
    >
      {ghost.entry && <span className="block truncate font-medium text-ink">{ghost.entry.description || "No description"}</span>}
      <span className="mono text-faint">
        {minToHHMM(ghost.startMin)} – {ghost.endMin >= DAY_MIN ? "24:00" : minToHHMM(ghost.endMin)}
      </span>
    </div>
  );
}
